<template>
  <div id="survey_form">
    <Navbar current="Pesquisas" :link="$paths.surveys.list" linkName="Pesquisas" />
    <Sidebar />
    <Container col="col-12">
      <Header
        :isSubHeader="false"
        :title="isEditing ? 'Editar pesquisa' : 'Nova pesquisa'"
      />

      <LoadingContainer v-if="loading" />

      <div v-else class="row">
        <div class="col-12 col-xl-9">
          <!-- Stepper -->
          <ul class="nav nav-pills mb-4">
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: step === 1 }"
                @click="step = 1"
              >
                1. Público
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link"
                :class="{ active: step === 2 }"
                :disabled="!filters.user_type"
                @click="step = 2"
              >
                2. Perguntas
              </button>
            </li>
          </ul>

          <!-- Passo 1: Filtro de público -->
          <div v-if="step === 1" class="card">
            <div class="card-body">
              <div class="row g-3">
                <div class="col-12 col-md-4">
                  <label class="form-label">Público *</label>
                  <select
                    class="form-select"
                    v-model="filters.user_type"
                    @change="schedulePreview"
                  >
                    <option value="">Selecione</option>
                    <option
                      v-for="opt in audienceOptions.user_types"
                      :key="opt.value"
                      :value="opt.value"
                    >
                      {{ opt.label }}
                    </option>
                  </select>
                </div>
                <div class="col-12 col-md-4">
                  <label class="form-label">Cadastro de</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="filters.created_from"
                    @change="schedulePreview"
                  />
                </div>
                <div class="col-12 col-md-4">
                  <label class="form-label">Cadastro até</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="filters.created_to"
                    @change="schedulePreview"
                  />
                </div>

                <div class="col-12">
                  <div class="form-check">
                    <input
                      type="checkbox"
                      class="form-check-input"
                      id="withoutAppointments"
                      v-model="filters.without_appointments"
                      @change="onWithoutAppointmentsChange"
                    />
                    <label class="form-check-label" for="withoutAppointments">
                      Sem nenhuma consulta concluída
                    </label>
                  </div>
                </div>

                <div class="col-12 col-md-6">
                  <label class="form-label">Última consulta de</label>
                  <input
                    type="date"
                    class="form-control"
                    :disabled="filters.without_appointments"
                    v-model="filters.last_appointment_from"
                    @change="schedulePreview"
                  />
                </div>
                <div class="col-12 col-md-6">
                  <label class="form-label">Última consulta até</label>
                  <input
                    type="date"
                    class="form-control"
                    :disabled="filters.without_appointments"
                    v-model="filters.last_appointment_to"
                    @change="schedulePreview"
                  />
                </div>

                <template v-if="isPsychologistAudience">
                  <div class="col-12 col-md-4">
                    <label class="form-label">Assinatura</label>
                    <select
                      class="form-select"
                      v-model="filters.subscription"
                      @change="schedulePreview"
                    >
                      <option value="">Todos</option>
                      <option
                        v-for="opt in audienceOptions.subscription"
                        :key="opt.value"
                        :value="opt.value"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                  </div>
                  <div class="col-12 col-md-4">
                    <label class="form-label">Aprovação</label>
                    <select
                      class="form-select"
                      v-model="filters.approved"
                      @change="schedulePreview"
                    >
                      <option value="">Todos</option>
                      <option :value="true">Aprovados</option>
                      <option :value="false">Não aprovados</option>
                    </select>
                  </div>
                  <div class="col-12 col-md-4" v-if="audienceOptions.plans?.length">
                    <label class="form-label">Planos</label>
                    <div class="d-flex flex-wrap gap-2">
                      <button
                        v-for="plan in audienceOptions.plans"
                        :key="plan.id"
                        type="button"
                        class="btn btn-sm"
                        :class="
                          filters.plan_ids.includes(plan.id)
                            ? 'btn-primary'
                            : 'btn-outline-secondary'
                        "
                        @click="togglePlan(plan.id)"
                      >
                        {{ plan.label }}
                      </button>
                    </div>
                  </div>
                </template>
              </div>

              <div class="d-flex justify-content-end mt-4">
                <button
                  class="btn btn-primary"
                  :disabled="!filters.user_type"
                  @click="goToQuestions"
                >
                  Avançar <i class="fa-solid fa-arrow-right ms-1"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Passo 2: Perguntas -->
          <div v-if="step === 2">
            <div class="card mb-3">
              <div class="card-body">
                <div class="mb-3">
                  <label class="form-label">Título da pesquisa *</label>
                  <input type="text" class="form-control" v-model="title" />
                </div>
                <div>
                  <label class="form-label">Descrição</label>
                  <textarea
                    class="form-control"
                    rows="2"
                    v-model="description"
                  ></textarea>
                </div>
              </div>
            </div>

            <div
              v-for="(q, qIndex) in questions"
              :key="qIndex"
              class="card mb-3"
            >
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-start mb-3">
                  <strong>Pergunta {{ qIndex + 1 }}</strong>
                  <button
                    v-if="questions.length > 1"
                    class="btn btn-sm btn-outline-danger"
                    @click="removeQuestion(qIndex)"
                  >
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </div>

                <div class="row g-3">
                  <div class="col-12 col-md-8">
                    <label class="form-label">Texto da pergunta</label>
                    <input type="text" class="form-control" v-model="q.title" />
                  </div>
                  <div class="col-12 col-md-4">
                    <label class="form-label">Tipo</label>
                    <select class="form-select" v-model="q.type">
                      <option
                        v-for="t in questionTypes"
                        :key="t.value"
                        :value="t.value"
                      >
                        {{ t.label }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="form-check mt-3">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :id="`required-${qIndex}`"
                    v-model="q.required"
                  />
                  <label class="form-check-label" :for="`required-${qIndex}`">
                    Resposta obrigatória
                  </label>
                </div>

                <div v-if="q.type !== 'open_text'" class="mt-3">
                  <label class="form-label">Alternativas</label>
                  <div
                    v-for="(opt, oIndex) in q.options"
                    :key="oIndex"
                    class="d-flex gap-2 mb-2"
                  >
                    <input
                      type="text"
                      class="form-control"
                      :placeholder="`Alternativa ${oIndex + 1}`"
                      v-model="q.options[oIndex]"
                    />
                    <button
                      v-if="q.options.length > 2"
                      class="btn btn-outline-secondary"
                      @click="removeOption(qIndex, oIndex)"
                    >
                      <i class="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    @click="addOption(qIndex)"
                  >
                    <i class="fa-solid fa-plus me-1"></i>Adicionar alternativa
                  </button>
                </div>

                <div v-if="questionError(q)" class="text-danger small mt-2">
                  {{ questionError(q) }}
                </div>
              </div>
            </div>

            <button class="btn btn-outline-primary mb-4" @click="addQuestion">
              <i class="fa-solid fa-plus me-1"></i>Adicionar pergunta
            </button>

            <div class="d-flex justify-content-between">
              <button class="btn btn-outline-secondary" @click="backToAudience">
                <i class="fa-solid fa-arrow-left me-1"></i>Voltar
              </button>
              <button
                class="btn btn-primary"
                :disabled="!canSubmit() || submitting"
                @click="submit"
              >
                <span
                  v-if="submitting"
                  class="spinner-border spinner-border-sm me-1"
                ></span>
                Salvar rascunho
              </button>
            </div>
          </div>
        </div>

        <!-- Prévia de público (sempre visível) -->
        <div class="col-12 col-xl-3">
          <div class="card sticky-top">
            <div class="card-header"><strong>Público estimado</strong></div>
            <div class="card-body text-center">
              <div v-if="previewLoading" class="text-muted">
                <div class="spinner-border spinner-border-sm me-1"></div>
                Calculando...
              </div>
              <template v-else-if="previewCount !== null">
                <div class="display-6 fw-bold">{{ previewCount }}</div>
                <p class="text-muted small mb-3">pessoas vão receber</p>
                <ul class="list-unstyled text-start small mb-0">
                  <li v-for="p in previewSample" :key="p.id" class="mb-1">
                    {{ p.name }}
                    <span class="text-muted">— {{ p.email }}</span>
                  </li>
                </ul>
              </template>
              <p v-else class="text-muted small mb-0">
                Selecione o público para ver a estimativa.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </div>
</template>

<script>
import SurveyFormController from "./SurveyFormController";
export default SurveyFormController;
</script>
